import { useState, useEffect, useRef } from "react";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  Sparkles, 
  Send,
  AlertTriangle,
  Calculator,
  MessageCircle,
  Lightbulb,
  Loader2,
  User,
  Bot,
  BookOpen,
  TrendingUp,
  ClipboardList,
  PlayCircle,
  Calendar,
  Heart,
  Target,
  PieChart,
  Trash2,
  Plus,
  ArrowRight
} from "lucide-react";
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip,
  CartesianGrid
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Mode configurations
const MODES = {
  explain: {
    label: "Explain",
    icon: BookOpen,
    description: "Learn financial concepts",
    color: "text-blue-500",
    bgColor: "bg-blue-50 dark:bg-blue-950/30"
  },
  reflect: {
    label: "Reflect",
    icon: TrendingUp,
    description: "Review your spending patterns",
    color: "text-teal-500",
    bgColor: "bg-teal-50 dark:bg-teal-950/30"
  },
  plan: {
    label: "Plan",
    icon: ClipboardList,
    description: "Get next-step suggestions",
    color: "text-purple-500",
    bgColor: "bg-purple-50 dark:bg-purple-950/30"
  },
  simulate: {
    label: "Simulate",
    icon: PlayCircle,
    description: "Run financial calculators",
    color: "text-amber-500",
    bgColor: "bg-amber-50 dark:bg-amber-950/30"
  }
};

export default function MentorPage() {
  const { household } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeMode, setActiveMode] = useState("explain");
  const [agenda, setAgenda] = useState(null);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Compound growth simulator state
  const [simPrincipal, setSimPrincipal] = useState(10000);
  const [simMonthly, setSimMonthly] = useState(500);
  const [simYears, setSimYears] = useState(10);
  const [simReturn, setSimReturn] = useState(7);
  const [simResults, setSimResults] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  // Debt payoff simulator state
  const [debts, setDebts] = useState([
    { name: "Credit Card", balance: 5000, interest_rate: 19.99, min_payment: 150 }
  ]);
  const [extraPayment, setExtraPayment] = useState(100);
  const [debtStrategy, setDebtStrategy] = useState("avalanche");
  const [debtResults, setDebtResults] = useState(null);
  const [debtLoading, setDebtLoading] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || !household) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");
    setMessages(prev => [...prev, { role: 'user', content: userMessage, mode: activeMode }]);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/mentor/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: userMessage, mode: activeMode })
      });

      const data = await response.json();
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response,
        disclaimer: data.disclaimer,
        type: data.type,
        mode: data.mode
      }]);
    } catch (error) {
      toast.error("Failed to get response");
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again.",
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const runGrowthSimulation = async () => {
    if (!household) return;
    setSimLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/mentor/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          principal: simPrincipal,
          monthly_contribution: simMonthly,
          years: simYears,
          annual_return: simReturn
        })
      });

      const data = await response.json();
      setSimResults(data);
    } catch (error) {
      toast.error("Simulation failed");
    } finally {
      setSimLoading(false);
    }
  };

  const runDebtSimulation = async () => {
    if (!household) return;
    setDebtLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/mentor/debt-payoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          debts: debts,
          extra_payment: extraPayment,
          strategy: debtStrategy
        })
      });

      const data = await response.json();
      setDebtResults(data);
    } catch (error) {
      toast.error("Debt simulation failed");
    } finally {
      setDebtLoading(false);
    }
  };

  const generateAgenda = async () => {
    if (!household) return;
    setAgendaLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/mentor/generate-agenda`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();
      setAgenda(data);
      toast.success("Money Date agenda generated");
    } catch (error) {
      toast.error("Failed to generate agenda");
    } finally {
      setAgendaLoading(false);
    }
  };

  const addDebt = () => {
    setDebts([...debts, { name: "", balance: 0, interest_rate: 0, min_payment: 0 }]);
  };

  const updateDebt = (index, field, value) => {
    const updated = [...debts];
    updated[index][field] = field === 'name' ? value : parseFloat(value) || 0;
    setDebts(updated);
  };

  const removeDebt = (index) => {
    setDebts(debts.filter((_, i) => i !== index));
  };

  const formatCurrency = (amount) => {
    const currency = household?.currency || 'NZD';
    return new Intl.NumberFormat('en-NZ', { 
      style: 'currency', 
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(amount);
  };

  const getAgendaIcon = (iconName) => {
    const icons = {
      'heart': Heart,
      'trending-up': TrendingUp,
      'pie-chart': PieChart,
      'calendar': Calendar,
      'target': Target,
      'sparkles': Sparkles
    };
    const Icon = icons[iconName] || Sparkles;
    return <Icon className="w-5 h-5" />;
  };

  const quickQuestions = {
    explain: [
      "What is compound interest?",
      "Explain the 50/30/20 budget rule",
      "What's the difference between good and bad debt?"
    ],
    reflect: [
      "How does my spending compare to income?",
      "What are my top spending categories?",
      "How am I progressing on my goals?"
    ],
    plan: [
      "What should I focus on next?",
      "How can I build an emergency fund?",
      "What's the best order to pay off debt?"
    ]
  };

  return (
    <AppLayout>
      <div className="p-6 lg:p-8" data-testid="mentor-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold">AI Financial Mentor</h1>
              <p className="text-muted-foreground">Educational guidance for your financial journey</p>
            </div>
          </div>
        </div>

        {/* Disclaimer Banner */}
        <div className="mb-6 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50" data-testid="disclaimer-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Educational Information Only
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                This AI mentor provides general financial education and concepts. It does not provide personalized financial advice. 
                For specific investment decisions, please consult a licensed financial adviser.
              </p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="chat" className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="chat" data-testid="tab-chat">
              <MessageCircle className="w-4 h-4 mr-2" />
              Chat
            </TabsTrigger>
            <TabsTrigger value="simulator" data-testid="tab-simulator">
              <Calculator className="w-4 h-4 mr-2" />
              Simulators
            </TabsTrigger>
            <TabsTrigger value="agenda" data-testid="tab-agenda">
              <Calendar className="w-4 h-4 mr-2" />
              Money Date
            </TabsTrigger>
            <TabsTrigger value="learn" data-testid="tab-learn">
              <Lightbulb className="w-4 h-4 mr-2" />
              Learn
            </TabsTrigger>
          </TabsList>

          {/* Chat Tab */}
          <TabsContent value="chat" className="space-y-4">
            {/* Mode Selector */}
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(MODES).filter(([key]) => key !== 'simulate').map(([key, mode]) => (
                <button
                  key={key}
                  onClick={() => setActiveMode(key)}
                  data-testid={`mode-${key}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                    activeMode === key
                      ? `${mode.bgColor} border-current ${mode.color} font-medium`
                      : 'border-border hover:border-muted-foreground/50 text-muted-foreground'
                  }`}
                >
                  <mode.icon className="w-4 h-4" />
                  <span>{mode.label}</span>
                </button>
              ))}
            </div>

            <div className="card-dashboard h-[500px] flex flex-col">
              {/* Messages */}
              <ScrollArea className="flex-1 pr-4">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-8">
                    <div className={`w-16 h-16 rounded-2xl ${MODES[activeMode].bgColor} flex items-center justify-center mb-4`}>
                      {(() => {
                        const ModeIcon = MODES[activeMode].icon;
                        return <ModeIcon className={`w-8 h-8 ${MODES[activeMode].color}`} />;
                      })()}
                    </div>
                    <h3 className="font-heading font-semibold mb-2">{MODES[activeMode].label} Mode</h3>
                    <p className="text-sm text-muted-foreground max-w-sm mb-6">
                      {MODES[activeMode].description}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 justify-center">
                      {quickQuestions[activeMode]?.map((q, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          onClick={() => setInputMessage(q)}
                          data-testid={`quick-question-${i}`}
                        >
                          {q}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 py-4">
                    {messages.map((msg, index) => (
                      <div 
                        key={index}
                        className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {msg.role === 'assistant' && (
                          <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-emerald-600" />
                          </div>
                        )}
                        
                        <div className={`max-w-[80%] rounded-2xl p-4 ${
                          msg.role === 'user' 
                            ? 'bg-emerald-600 text-white rounded-br-md' 
                            : msg.type === 'refusal'
                              ? 'bg-amber-50 dark:bg-amber-900/30 border border-amber-200 rounded-bl-md'
                              : 'bg-muted rounded-bl-md'
                        }`}>
                          {msg.role === 'user' && msg.mode && (
                            <Badge variant="secondary" className="mb-2 text-xs bg-white/20 text-white">
                              {MODES[msg.mode]?.label}
                            </Badge>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          
                          {msg.disclaimer && (
                            <p className="text-xs mt-3 pt-3 border-t border-current/20 opacity-70">
                              {msg.disclaimer}
                            </p>
                          )}
                        </div>
                        
                        {msg.role === 'user' && (
                          <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {loading && (
                      <div className="flex gap-3 justify-start">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="bg-muted rounded-2xl rounded-bl-md p-4">
                          <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                        </div>
                      </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="border-t border-border pt-4 mt-4">
                <div className="flex gap-3">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder={`Ask about financial concepts (${MODES[activeMode].label} mode)...`}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    disabled={loading}
                    data-testid="chat-input"
                  />
                  <Button 
                    onClick={sendMessage} 
                    disabled={loading || !inputMessage.trim()}
                    className="btn-primary"
                    data-testid="send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Simulator Tab */}
          <TabsContent value="simulator" className="space-y-6">
            <Tabs defaultValue="growth" className="space-y-4">
              <TabsList>
                <TabsTrigger value="growth">Compound Growth</TabsTrigger>
                <TabsTrigger value="debt">Debt Payoff</TabsTrigger>
              </TabsList>

              {/* Compound Growth Simulator */}
              <TabsContent value="growth">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="card-dashboard">
                    <h3 className="font-heading font-semibold mb-6">Compound Growth Simulator</h3>
                    
                    <div className="space-y-6">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Starting Amount</Label>
                          <span className="font-medium">{formatCurrency(simPrincipal)}</span>
                        </div>
                        <Slider
                          value={[simPrincipal]}
                          onValueChange={([v]) => setSimPrincipal(v)}
                          min={0}
                          max={100000}
                          step={1000}
                          data-testid="principal-slider"
                        />
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Monthly Contribution</Label>
                          <span className="font-medium">{formatCurrency(simMonthly)}</span>
                        </div>
                        <Slider
                          value={[simMonthly]}
                          onValueChange={([v]) => setSimMonthly(v)}
                          min={0}
                          max={5000}
                          step={50}
                          data-testid="monthly-slider"
                        />
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Time Horizon</Label>
                          <span className="font-medium">{simYears} years</span>
                        </div>
                        <Slider
                          value={[simYears]}
                          onValueChange={([v]) => setSimYears(v)}
                          min={1}
                          max={40}
                          step={1}
                          data-testid="years-slider"
                        />
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Assumed Annual Return</Label>
                          <span className="font-medium">{simReturn}%</span>
                        </div>
                        <Slider
                          value={[simReturn]}
                          onValueChange={([v]) => setSimReturn(v)}
                          min={1}
                          max={15}
                          step={0.5}
                          data-testid="return-slider"
                        />
                      </div>

                      <Button 
                        onClick={runGrowthSimulation} 
                        className="btn-primary w-full"
                        disabled={simLoading}
                        data-testid="run-growth-simulation-btn"
                      >
                        {simLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Calculator className="w-4 h-4 mr-2" />
                            Run Simulation
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="card-dashboard">
                    <h3 className="font-heading font-semibold mb-6">Projected Growth</h3>
                    
                    {simResults ? (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/30">
                            <p className="text-sm text-muted-foreground">Final Balance</p>
                            <p className="font-heading text-2xl font-bold text-emerald-600" data-testid="final-balance">
                              {formatCurrency(simResults.final_balance)}
                            </p>
                          </div>
                          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/30">
                            <p className="text-sm text-muted-foreground">Total Growth</p>
                            <p className="font-heading text-2xl font-bold text-amber-600" data-testid="total-growth">
                              {formatCurrency(simResults.total_growth)}
                            </p>
                          </div>
                        </div>

                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={simResults.projections}>
                              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                              <XAxis 
                                dataKey="year" 
                                tick={{ fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis 
                                tick={{ fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
                              />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload?.length) {
                                    const data = payload[0].payload;
                                    return (
                                      <div className="bg-card px-4 py-3 rounded-lg shadow-lg border border-border">
                                        <p className="font-medium">Year {data.year}</p>
                                        <p className="text-sm">Balance: {formatCurrency(data.balance)}</p>
                                        <p className="text-sm text-emerald-600">Growth: {formatCurrency(data.growth)}</p>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="balance" 
                                stroke="#10B981" 
                                strokeWidth={2}
                                dot={false}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="contributions" 
                                stroke="#F59E0B" 
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>

                        <p className="text-xs text-muted-foreground mt-4 p-3 rounded-lg bg-muted/50">
                          {simResults.disclaimer}
                        </p>
                      </>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-muted-foreground">
                        <div className="text-center">
                          <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>Adjust the inputs and run the simulation</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Debt Payoff Simulator */}
              <TabsContent value="debt">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="card-dashboard">
                    <h3 className="font-heading font-semibold mb-6">Debt Payoff Simulator</h3>
                    
                    <div className="space-y-4">
                      {/* Debts */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Your Debts</Label>
                          <Button variant="outline" size="sm" onClick={addDebt}>
                            <Plus className="w-4 h-4 mr-1" /> Add Debt
                          </Button>
                        </div>
                        
                        {debts.map((debt, index) => (
                          <div key={index} className="p-3 rounded-lg bg-muted/50 space-y-2">
                            <div className="flex items-center gap-2">
                              <Input
                                value={debt.name}
                                onChange={(e) => updateDebt(index, 'name', e.target.value)}
                                placeholder="Debt name"
                                className="flex-1"
                              />
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => removeDebt(index)}
                                disabled={debts.length === 1}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <Label className="text-xs">Balance</Label>
                                <Input
                                  type="number"
                                  value={debt.balance}
                                  onChange={(e) => updateDebt(index, 'balance', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Interest %</Label>
                                <Input
                                  type="number"
                                  value={debt.interest_rate}
                                  onChange={(e) => updateDebt(index, 'interest_rate', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Min Payment</Label>
                                <Input
                                  type="number"
                                  value={debt.min_payment}
                                  onChange={(e) => updateDebt(index, 'min_payment', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Extra Payment */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Extra Monthly Payment</Label>
                          <span className="font-medium">{formatCurrency(extraPayment)}</span>
                        </div>
                        <Slider
                          value={[extraPayment]}
                          onValueChange={([v]) => setExtraPayment(v)}
                          min={0}
                          max={1000}
                          step={25}
                        />
                      </div>

                      {/* Strategy */}
                      <div className="space-y-2">
                        <Label>Payoff Strategy</Label>
                        <Select value={debtStrategy} onValueChange={setDebtStrategy}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="avalanche">Avalanche (Highest Interest First)</SelectItem>
                            <SelectItem value="snowball">Snowball (Smallest Balance First)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button 
                        onClick={runDebtSimulation} 
                        className="btn-primary w-full"
                        disabled={debtLoading}
                        data-testid="run-debt-simulation-btn"
                      >
                        {debtLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Calculator className="w-4 h-4 mr-2" />
                            Calculate Payoff
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="card-dashboard">
                    <h3 className="font-heading font-semibold mb-6">Payoff Timeline</h3>
                    
                    {debtResults ? (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/30">
                            <p className="text-sm text-muted-foreground">Debt Free In</p>
                            <p className="font-heading text-2xl font-bold text-green-600">
                              {debtResults.total_years} years
                            </p>
                            <p className="text-xs text-muted-foreground">{debtResults.total_months} months</p>
                          </div>
                          <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/30">
                            <p className="text-sm text-muted-foreground">Strategy</p>
                            <p className="font-heading text-xl font-bold text-blue-600 capitalize">
                              {debtResults.strategy}
                            </p>
                          </div>
                        </div>

                        {/* Payoff Order */}
                        <div className="mb-6">
                          <h4 className="text-sm font-medium mb-3">Payoff Order</h4>
                          <div className="space-y-2">
                            {debtResults.payoff_order?.map((debt, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">
                                  {index + 1}
                                </div>
                                <span className="flex-1">{debt.name}</span>
                                <Badge variant="outline">Month {debt.paid_off_month}</Badge>
                              </div>
                            ))}
                          </div>
                        </div>

                        {debtResults.timeline?.length > 0 && (
                          <div className="h-48">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={debtResults.timeline}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis 
                                  dataKey="month" 
                                  tick={{ fontSize: 12 }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <YAxis 
                                  tick={{ fontSize: 12 }}
                                  axisLine={false}
                                  tickLine={false}
                                  tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
                                />
                                <Tooltip
                                  content={({ active, payload }) => {
                                    if (active && payload?.length) {
                                      const data = payload[0].payload;
                                      return (
                                        <div className="bg-card px-4 py-3 rounded-lg shadow-lg border border-border">
                                          <p className="font-medium">Month {data.month}</p>
                                          <p className="text-sm">Remaining: {formatCurrency(data.total_remaining)}</p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="total_remaining" 
                                  stroke="#10B981" 
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        )}

                        <p className="text-xs text-muted-foreground mt-4 p-3 rounded-lg bg-muted/50">
                          {debtResults.disclaimer}
                        </p>
                      </>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-muted-foreground">
                        <div className="text-center">
                          <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>Enter your debts and run the simulation</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Money Date Agenda Tab */}
          <TabsContent value="agenda" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-pink-500" />
                      Money Date Agenda
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Auto-generate a neutral discussion agenda from your financial data
                    </p>
                  </div>
                  <Button 
                    onClick={generateAgenda}
                    disabled={agendaLoading}
                    className="btn-primary"
                    data-testid="generate-agenda-btn"
                  >
                    {agendaLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Generate Agenda
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {agenda ? (
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="p-4 rounded-xl bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-950/30 dark:to-purple-950/30 border border-pink-100 dark:border-pink-900">
                      <h3 className="font-heading text-lg font-semibold mb-1">
                        {agenda.period} Money Date
                      </h3>
                      <p className="text-sm text-muted-foreground">{agenda.household_name}</p>
                    </div>

                    {/* Agenda Items */}
                    <div className="space-y-4">
                      {agenda.agenda?.map((section, index) => (
                        <div key={index} className="p-4 rounded-xl border border-border" data-testid={`agenda-section-${index}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                              {getAgendaIcon(section.icon)}
                            </div>
                            <h4 className="font-semibold">{section.section}</h4>
                          </div>
                          <ul className="space-y-2 pl-10">
                            {section.items.map((item, itemIndex) => (
                              <li key={itemIndex} className="text-sm flex items-start gap-2">
                                <ArrowRight className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    {/* Disclaimer */}
                    <p className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/50">
                      {agenda.disclaimer}
                    </p>
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                    <h3 className="font-semibold mb-2">No Agenda Generated Yet</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      Click "Generate Agenda" to create a neutral discussion guide based on your month's financial data.
                      Perfect for couples who want to have productive money conversations.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Learn Tab */}
          <TabsContent value="learn" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: "Emergency Fund Basics",
                  description: "Why you need 3-6 months of expenses saved",
                  question: "Explain emergency funds and how much I should save",
                  mode: "explain"
                },
                {
                  title: "Debt Repayment Strategies",
                  description: "Avalanche vs Snowball method explained",
                  question: "What's the difference between debt avalanche and snowball methods?",
                  mode: "explain"
                },
                {
                  title: "The 50/30/20 Rule",
                  description: "A simple budgeting framework",
                  question: "Explain the 50/30/20 budgeting rule",
                  mode: "explain"
                },
                {
                  title: "Compound Interest",
                  description: "The eighth wonder of the world",
                  question: "Explain compound interest with examples",
                  mode: "explain"
                },
                {
                  title: "Review My Spending",
                  description: "Get insights on your spending patterns",
                  question: "What are my top spending categories this month?",
                  mode: "reflect"
                },
                {
                  title: "What Should I Focus On?",
                  description: "Get prioritized next steps",
                  question: "Based on my finances, what should I focus on next?",
                  mode: "plan"
                }
              ].map((topic, index) => (
                <div 
                  key={index}
                  className="card-dashboard hover-lift cursor-pointer"
                  onClick={() => {
                    setActiveMode(topic.mode);
                    setInputMessage(topic.question);
                    document.querySelector('[data-testid="tab-chat"]')?.click();
                  }}
                  data-testid={`learn-topic-${index}`}
                >
                  <div className={`w-10 h-10 rounded-xl ${MODES[topic.mode].bgColor} flex items-center justify-center mb-4`}>
                    {(() => {
                      const ModeIcon = MODES[topic.mode].icon;
                      return <ModeIcon className={`w-5 h-5 ${MODES[topic.mode].color}`} />;
                    })()}
                  </div>
                  <h4 className="font-heading font-semibold mb-2">{topic.title}</h4>
                  <p className="text-sm text-muted-foreground">{topic.description}</p>
                  <Badge variant="outline" className="mt-3 text-xs">{MODES[topic.mode].label} Mode</Badge>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
